import React, { createContext, useContext, useState } from "react";
import { Menu, MenuItem, IconButton, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Contexto para compartir estado del menú entre los sub-componentes
const DropdownMenuContext = createContext({});

export { DropdownMenuContext };

export const DropdownMenu = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <DropdownMenuContext.Provider
      value={{ anchorEl, open, handleOpen, handleClose }}
    >
      {children}
    </DropdownMenuContext.Provider>
  );
};

// Modificamos Trigger para usar el contexto
export const DropdownMenuTrigger = React.forwardRef(
  ({ children, asChild, ...props }, ref) => {
    const { handleOpen } = useContext(DropdownMenuContext);

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ref,
        onClick: handleOpen,
        ...props,
      });
    }

    return (
      <IconButton ref={ref} onClick={handleOpen} sx={{ cursor: "pointer" }} {...props}>
        {children}
      </IconButton>
    );
  }
);

DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

// Modificamos Content para consumir estado del menú (se pueden sobreescribir props)
const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null) return [];
  return [value];
};

export const DropdownMenuContent = ({
  children,
  align = "start",
  sideOffset = 4,
  anchorEl: anchorElProp,
  open: openProp,
  onClose: onCloseProp,
  sx = {},
  slotProps: slotPropsProp,
  ...props
}) => {
  const { anchorEl, open, handleClose } = useContext(DropdownMenuContext);

  const finalAnchorEl = anchorElProp !== undefined ? anchorElProp : anchorEl;
  const finalOpen = openProp !== undefined ? openProp : open;
  const finalOnClose = onCloseProp || handleClose;

  const theme = useTheme();
  const slotProps = slotPropsProp || {};
  const menuZIndex = Math.max(theme.zIndex?.modal ?? 1300, 10000) + 2;

  const basePaperSx = {
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    minWidth: "8rem",
    padding: "4px",
  };

  const mergedSlotProps = {
    ...slotProps,
    root: {
      ...slotProps.root,
      sx: [
        { zIndex: menuZIndex },
        ...ensureArray(slotProps.root?.sx),
      ],
    },
    paper: {
      ...slotProps.paper,
      sx: [
        basePaperSx,
        ...ensureArray(slotProps.paper?.sx),
        ...ensureArray(sx),
      ],
    },
  };

  return (
    <Menu
      anchorEl={finalAnchorEl}
      open={finalOpen}
      onClose={finalOnClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: align === "end" ? "right" : "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: align === "end" ? "right" : "left",
      }}
      MenuListProps={{
        sx: {
          padding: "5px 0",
        },
      }}
      slotProps={mergedSlotProps}
      {...props}
    >
      {children}
    </Menu>
  );
};

// Modificamos Item para cerrar el menú después del clic (si corresponde)
export const DropdownMenuItem = ({
  children,
  onClick,
  className,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const { handleClose } = useContext(DropdownMenuContext);

  const isDestructive = className?.includes("text-destructive");

  const handleItemClick = (event) => {
    if (onClick) onClick(event);
    handleClose();
  };

  return (
    <MenuItem
      onClick={handleItemClick}
      sx={{
        fontSize: "0.875rem",
        padding: "4px 12px !important",
        borderRadius: "4px",
        minHeight: "auto",
        color: isDestructive
          ? theme.palette.error.main
          : theme.palette.text.primary,
        gap: "8px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: isDestructive
            ? theme.palette.error.light + "20"
            : theme.palette.action.hover,
          cursor: "pointer",
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </MenuItem>
  );
};

export const DropdownMenuLabel = ({ children, sx = {}, ...props }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        padding: "6px 12px",
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        fontWeight: 500,
        pointerEvents: "none",
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export const DropdownMenuSeparator = ({ sx = {}, ...props }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        height: "1px",
        backgroundColor: theme.palette.divider,
        margin: "4px 0",
        ...sx,
      }}
      {...props}
    />
  );
};
